import type { PdfPagePreview } from "@estada/plan-analyzer";
import type { C1_FloorPdfSource, C1_PointData } from "../types/C1_ModelTypes";
import type { Rect } from "../types/C1_AppTypes";

/** Reveal the original PDF above 50% by fading the editable building structure. */
export function pdfStructureOpacity(pdfOpacity: number): number {
  if (!Number.isFinite(pdfOpacity)) return 1;
  return 1 - Math.max(0, Math.min(1, (pdfOpacity - 50) / 50)) * 0.8;
}

/** Frame the complete PDF independently of the axes retained by room cleanup. */
export function pdfDrawingBounds(bounds: Rect, source: C1_FloorPdfSource | undefined, pageSize = source?.pageSize): Rect {
  if (!source || !pageSize || ![pageSize.width, pageSize.height, source.scale].every(value => Number.isFinite(value) && value > 0)) return bounds;
  const metresPerPoint = source.scale * 0.0254 / 72;
  return {
    x1: Math.min(bounds.x1, -source.originPdf.x * metresPerPoint),
    y1: Math.min(bounds.y1, -source.originPdf.y * metresPerPoint),
    x2: Math.max(bounds.x2, (pageSize.width - source.originPdf.x) * metresPerPoint),
    y2: Math.max(bounds.y2, (pageSize.height - source.originPdf.y) * metresPerPoint),
  };
}

export type C1_PdfOverlay = {
  floorId: string;
  documentId: string;
  page: number;
  width: number;
  height: number;
  svgContent: string;
};

export function isPdfOverlayActive(
  floor: { id: string; pdfSource?: C1_FloorPdfSource } | undefined,
  overlay: C1_PdfOverlay | null,
): boolean {
  const source = floor?.pdfSource;
  return (
    !!source &&
    !!overlay &&
    overlay.floorId === floor?.id &&
    overlay.documentId === source.documentId &&
    overlay.page === source.page
  );
}

/** Keep only the original drawing: the preview's white page must not cover the editable plan. */
export function pdfOverlayContent(preview: PdfPagePreview): string {
  const document = new DOMParser().parseFromString(preview.svg, "image/svg+xml");
  const original = document.querySelector('[data-layer="original"]');
  if (document.querySelector("parsererror") || !original)
    throw new Error("Die PDF-Grafik konnte nicht gelesen werden.");
  return new XMLSerializer().serializeToString(original);
}

export function pdfOverlayTransform(source: C1_FloorPdfSource, view: C1_PointData, svgPx: number): string {
  const scale = ((source.scale * 0.0254) / 72) * svgPx;
  return `translate(${-source.originPdf.x * scale - view.x * svgPx} ${-source.originPdf.y * scale - view.y * svgPx}) scale(${scale})`;
}

/** PdfFile supports binary strings as well as buffers; always give the worker a fresh byte array. */
export async function pdfOverlayBytes(data: unknown): Promise<Uint8Array> {
  if (typeof data === "string") return Uint8Array.from(data, (character) => character.charCodeAt(0));
  if (data instanceof Uint8Array) return data.slice();
  if (data instanceof ArrayBuffer) return new Uint8Array(data.slice(0));
  if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
  throw new Error("Die Original-PDF ist im Projekt nicht verfügbar.");
}
